import { json_headers, request } from '~/services/common';

import { readSseResponse } from '~/utils/sse/readSseStream';

/**
 * Carries `status` and `code` so callers can branch on the failure instead of
 * pattern-matching prose. `message` keeps its `HTTP <status> <text>: <detail>`
 * shape for the generic paths that already render it.
 */
export class SseHttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Server prose, without the `HTTP <status>` prefix. */
    readonly detail: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'SseHttpError';
  }
}

/**
 * POST + consume SSE from `response.body` (native EventSource is GET-only and cannot send a JSON body).
 * Parses `text/event-stream` the same way as ChatGPT-style APIs: split events on blank lines, read `event:` / `data:`.
 */
export async function fetchSsePost(
  url: string,
  body: Record<string, unknown>,
  onEvent: (eventName: string, data: string) => void,
  options?: { signal?: AbortSignal },
): Promise<void> {
  const response = await request(url, {
    method: 'POST',
    headers: {
      ...json_headers,
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(body),
    returnResponse: true,
    // Skip JSON error parsing for non-OK bodies (502/504 often return HTML, which produced "Garbled response").
    allowNonOkResponse: true,
    /** POST + 302/303 can be followed with GET; `/ask` must stay POST-only. */
    redirect: 'error',
    signal: options?.signal,
  });

  if (!(response instanceof Response)) {
    throw new Error('Expected Response from fetch');
  }

  if (!response.ok) {
    let detail = '';
    let code: string | undefined;
    try {
      const text = (await response.text()).trim().slice(0, 400);
      if (text) {
        try {
          const parsed = JSON.parse(text) as { message?: string; error?: string; code?: string };
          detail =
            (typeof parsed.message === 'string' && parsed.message) ||
            (typeof parsed.error === 'string' && parsed.error) ||
            text;
          code = typeof parsed.code === 'string' ? parsed.code : undefined;
        } catch {
          detail = text;
        }
      }
    } catch {
      /* ignore */
    }
    const suffix = detail ? `: ${detail}` : '';
    throw new SseHttpError(
      `HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ''}${suffix}`.trim(),
      response.status,
      detail,
      code,
    );
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('text/event-stream')) {
    throw new Error(`Expected text/event-stream; received ${contentType || 'unknown'}`);
  }

  await readSseResponse(response, onEvent);
}
